import React, { FC, InputHTMLAttributes } from 'react';
import ErrorMessage from './ErrorMessage';
import styled from 'styled-components';
import { Label } from '../primitive/Label';

export type RadioLabelType = 'primary' | 'default';

interface RadioFieldProps extends InputHTMLAttributes<HTMLInputElement> {
    error?: string;
    variant?: RadioLabelType;
}

const RadioLabel: FC<React.PropsWithChildren<RadioFieldProps>> = ({
    variant = 'default',
    error,
    children,
    className,
    ...props
}) => (
    <>
        <Container hasError={!!error} variant={variant} className={className}>
            <HiddenRadio {...props} type="radio" />
            <StyledLabel variant="regular" htmlFor={props.id} radioVariant={variant}>
                {children}
            </StyledLabel>
        </Container>
        {error && <ErrorMessage>{error}</ErrorMessage>}
    </>
);

export default RadioLabel;

const HiddenRadio = styled.input.attrs(() => ({ type: 'radio' }))`
    position: absolute;
    opacity: 0;
`;

const StyledLabel = styled(Label)<{ radioVariant: RadioLabelType }>`
    font-weight: 400;
    z-index: 1;
    font-family: ${({ theme }) => theme.fontFamily.body};
    color: ${({ theme }) => theme.colors.neutral['100']};
    border-radius: 24px;
    text-align: center;
    pointer-events: none;
    padding: 0.4rem 1.6rem;
    display: block;

    ${({ radioVariant, theme }) =>
        radioVariant === 'default' &&
        `background-color: ${theme.colors.neutral['10']};
        border: 0.1rem solid ${theme.colors.neutral['10']};
    `}

    ${({ radioVariant, theme }) =>
        radioVariant === 'primary' &&
        `border: 0.1rem solid ${theme.colors.neutral['20']};
    `}

    ${HiddenRadio}:disabled ~ & {
        color: ${({ theme }) => theme.colors.neutral['30']};
    }
`;

const Container = styled.label<{ hasError: boolean; variant: RadioLabelType }>`
    ${HiddenRadio}:checked + ${StyledLabel} {
        ${({ variant, theme }) =>
            variant === 'default' &&
            `background-color: ${theme.colors.neutral['0']};
        border-color: ${theme.colors.neutral['40']};`}

        ${({ variant, theme }) =>
            variant === 'primary' &&
            `background-color: ${theme.colors.primary['80']};
        border: 0.1rem solid ${theme.colors.primary['80']};
        color: ${theme.colors.neutral['0']}
    `}
    }
`;
